import { useState } from 'react';
import { ASSETS } from './assets';
import { ScrollRollTop, ScrollRollBottom } from './ScrollDecorations';

export function AncientScrollView({ piece, crossRefs, onClose, onNavigate, isStudied, onNext, onPrev, currentIndex, totalPieces, onOpenBookSection, allPieces, studiedCount, discount, selectedBook }) {
  const [bibleVersion, setBibleVersion] = useState('esv');
  const [showDealTip, setShowDealTip] = useState(true);
  if (!piece) return null;
  const scriptureText = piece.scriptures?.[bibleVersion] || piece.scripture_text || '';

  // Use discount-based pricing if available, otherwise progressive pricing fallback
  const hasDiscount = discount && discount.discount_pct > 0;
  const currentPrice = hasDiscount ? discount.final_price : (() => {
    const advancementPrices = [4.99, 5.99, 6.99, 7.99, 8.99, 9.99, 10.99, 11.99, 12.99, 14.99];
    return advancementPrices[Math.min((studiedCount || 0), advancementPrices.length - 1)];
  })();
  const isMaxPrice = !hasDiscount && currentPrice >= 14.99;
  const savingsText = hasDiscount
    ? `${discount.discount_pct}% earned`
    : (isMaxPrice ? null : `Save $${(14.99 - currentPrice).toFixed(2)}`);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 scroll-unroll" key={piece.piece_id}>
      <div className="flex items-center justify-between mb-6 fade-up">
        <button onClick={onClose} data-testid="btn-close-scroll" className="flex items-center gap-2 text-amber-600 hover:text-amber-300 transition-colors text-sm">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Back to the Map
        </button>
      </div>

      {/* Main layout: scroll on left, buy panel on right (desktop) */}
      <div className="flex gap-6 items-start">

        {/* Scroll content */}
        <div className="flex-1 min-w-0">
          <div className="relative rounded-2xl overflow-hidden" style={{ boxShadow: '0 0 80px rgba(217,119,6,0.1), 0 30px 80px rgba(0,0,0,0.6)' }}>
            <ScrollRollTop />
            <div className="relative" style={{ background: 'linear-gradient(180deg, #1a1408 0%, #15110a 20%, #110e08 50%, #0d0a06 80%, #0a0805 100%)' }}>
              <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: `url(${ASSETS.scrollDark})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
              <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-amber-800/0 via-amber-800/20 to-amber-800/0" />
              <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-amber-800/0 via-amber-800/20 to-amber-800/0" />

              <div className="px-6 sm:px-10 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-5 fade-up">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="absolute inset-0 rounded-full blur-lg" style={{ backgroundColor: piece.color, opacity: 0.3 }} />
                      <span className="relative w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: piece.color }}>{piece.number}</span>
                    </div>
                    <div>
                      <span className="text-[10px] tracking-[0.25em] uppercase font-serif block" style={{ color: piece.color }}>{piece.title}</span>
                      <span className="text-[10px] text-amber-500/70 tracking-wider uppercase font-mono">{piece.keyword}</span>
                    </div>
                  </div>
                  {isStudied && (
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-900/20 border border-green-700/20" data-testid="scroll-studied-badge">
                      <svg className="w-3.5 h-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      <span className="text-[10px] text-green-400/70 font-mono tracking-wider">SCROLL STUDIED</span>
                    </div>
                  )}
                </div>

                <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-2 fade-up"
                  style={{ background: 'linear-gradient(180deg, #fde68a, #d97706)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {piece.reference}
                </h2>

                <div className="mb-6 py-4 border-y border-amber-800/15 fade-up">
                  <p className="font-serif italic text-lg sm:text-xl text-amber-200 leading-relaxed">"{piece.anchor}"</p>
                </div>

                {/* Bible Version Selector */}
                {piece.scriptures && (
                  <div className="flex items-center gap-2 mb-4 fade-up" data-testid="bible-version-selector">
                    <span className="text-[9px] tracking-wider uppercase text-amber-700/50 font-mono mr-1">Version:</span>
                    {['esv', 'kjv', 'nkjv'].map(v => (
                      <button key={v} onClick={() => setBibleVersion(v)}
                        data-testid={`btn-version-${v}`}
                        className={`px-3 py-1 rounded-full text-[10px] font-mono tracking-wider uppercase transition-all duration-300 ${
                          bibleVersion === v
                            ? 'bg-amber-800/30 text-amber-200 border border-amber-600/40'
                            : 'text-amber-700/50 border border-amber-900/15 hover:text-amber-400 hover:border-amber-800/30'
                        }`}>
                        {v}
                      </button>
                    ))}
                  </div>
                )}

                {/* Scripture Text */}
                {scriptureText && (
                  <div className="mb-6 fade-up">
                    <p className="text-[10px] tracking-[0.15em] uppercase text-amber-500/60 mb-3 font-mono">Scripture Reading</p>
                    <blockquote className="text-amber-100 leading-[2] text-base font-serif pl-4 border-l-2 border-amber-600/40">
                      {scriptureText}
                    </blockquote>
                  </div>
                )}

                {/* Commentary */}
                <div className="mb-6 fade-up">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-amber-500/50 mb-2 font-mono">What This Means</p>
                  <p className="text-amber-200/60 leading-relaxed text-sm font-serif">{piece.summary}</p>
                </div>

                {/* Nav buttons */}
                <div className="flex items-center justify-between gap-2 mb-8 fade-up">
                  {onPrev ? (
                    <button onClick={onPrev} data-testid="btn-prev-piece"
                      className="flex items-center gap-1.5 px-4 py-2.5 rounded-full transition-all duration-300 font-serif text-sm border border-amber-900/30 text-amber-400 hover:border-amber-600/50 hover:text-amber-200">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                      Prev
                    </button>
                  ) : <div className="w-20" />}

                  {onOpenBookSection ? (
                    <button onClick={onOpenBookSection} data-testid="btn-open-book-section"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-300 font-serif text-sm border border-amber-800/30 text-amber-300 hover:border-amber-600/50 hover:text-amber-100"
                      style={{ background: 'linear-gradient(135deg, rgba(120,53,15,0.12), rgba(217,119,6,0.04))' }}>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                      The Book Says
                    </button>
                  ) : (
                    <span className="text-amber-600/40 text-xs font-mono">{currentIndex + 1} of {totalPieces}</span>
                  )}

                  {onNext ? (
                    <button onClick={onNext} data-testid="btn-next-piece"
                      className="flex items-center gap-1.5 px-4 py-2.5 rounded-full transition-all duration-300 font-serif text-sm"
                      style={{ border: '1px solid rgba(217,119,6,0.4)', background: 'linear-gradient(135deg, rgba(120,53,15,0.15), rgba(217,119,6,0.05))', color: '#fbbf24' }}>
                      Next
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  ) : <div className="w-20" />}
                </div>

                {/* Connected Pieces */}
                {piece.connects_to?.length > 0 && (
              <div className="mb-8 fade-up">
                <p className="text-[10px] tracking-[0.2em] uppercase text-amber-700/50 mb-3 font-serif">Connects To</p>
                <div className="flex flex-wrap gap-2">
                  {piece.connects_to.map(connId => {
                    const connected = (allPieces || []).find(p => p.piece_id === connId);
                    return (
                      <span key={connId} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs border border-amber-900/20 bg-amber-950/20 text-amber-300/60 font-serif">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: connected?.color || '#d97706' }} />
                        {connected?.reference || connId}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* FOR DEEPER STUDY — Two-tier flow */}
            <div className="mt-8 pt-6 border-t border-amber-800/20 fade-up">
              <p className="text-[10px] tracking-[0.2em] uppercase text-amber-500/50 mb-4 font-serif">For Deeper Study</p>

              {/* Scholar-Level Appendices */}
              {crossRefs.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[9px] tracking-[0.15em] uppercase text-amber-600/50 mb-2 font-mono">Scholar-Level Appendices</p>
                  {crossRefs.slice(0, 5).map((ref, i) => (
                    <button key={i}
                      onClick={() => onNavigate(`/ebooks/${ref.target_book_id}/${ref.target_chapter_order || 1}`)}
                      data-testid={`scroll-ref-${ref.appendix_code || i}`}
                      className="flex items-center gap-3 w-full text-left p-3 rounded-lg transition-all duration-300 group/ref border border-transparent hover:border-amber-800/25"
                      style={{ background: 'linear-gradient(135deg, rgba(15,10,5,0.8), rgba(20,15,8,0.5))' }}>
                      <span className="flex-shrink-0 px-2 py-1 rounded text-[10px] font-bold font-mono" style={{ backgroundColor: `${piece.color}15`, color: piece.color }}>{ref.appendix_code}</span>
                      <span className="text-sm text-amber-200/50 group-hover/ref:text-amber-100 transition-colors font-serif">{ref.description?.substring(0, 80) || ref.target_chapter_title}</span>
                      <svg className="w-4 h-4 text-amber-800 group-hover/ref:text-amber-400 flex-shrink-0 ml-auto transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <ScrollRollBottom />
      </div>
        </div>{/* end flex-1 scroll content */}

        {/* BUY PANEL — Desktop/iPad sidebar */}
        <div className="hidden lg:block w-64 flex-shrink-0 sticky top-6" data-testid="buy-sidebar">
          <div className="rounded-2xl border-2 overflow-hidden"
            style={{ background: 'linear-gradient(180deg, rgba(15,10,4,0.98), rgba(10,7,3,0.98))', borderColor: 'rgba(217,119,6,0.3)', boxShadow: '0 8px 40px rgba(0,0,0,0.5), 0 0 30px rgba(217,119,6,0.08)' }}>
            
            {/* Scroll image */}
            <div className="h-28 relative overflow-hidden">
              <img src="/images/scroll.jpeg" alt="The King's Return" className="w-full h-full object-cover opacity-50" />
              <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#0a0703]" />
            </div>

            <div className="px-5 pb-5 -mt-4 relative z-10">
              <p className="text-[8px] tracking-[0.3em] uppercase font-mono text-amber-600/60 mb-1">{selectedBook?.title || ''}</p>
              <p className="text-lg font-serif font-bold text-amber-300 mb-1">Ebook</p>
              
              {/* Price */}
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-2xl font-bold" style={{ color: hasDiscount ? '#34d399' : '#fcd34d' }}>${currentPrice.toFixed(2)}</span>
                {(hasDiscount || !isMaxPrice) && (
                  <span className="text-[10px] text-red-400/70 line-through font-mono">$14.99</span>
                )}
              </div>

              {/* Deal balloon */}
              {showDealTip && (hasDiscount || !isMaxPrice) && (
                <div className="relative mb-3 p-3 rounded-lg border" data-testid="deal-balloon"
                  style={{ background: hasDiscount ? 'rgba(16,185,129,0.08)' : 'rgba(217,119,6,0.08)', borderColor: hasDiscount ? 'rgba(16,185,129,0.2)' : 'rgba(217,119,6,0.2)' }}>
                  <button onClick={() => setShowDealTip(false)} className="absolute top-1 right-1.5 text-amber-700/40 hover:text-amber-400 text-xs">&times;</button>
                  {hasDiscount ? (
                    <>
                      <p className="text-[11px] text-emerald-200/80 font-serif leading-relaxed">
                        Your study has earned you <strong className="text-emerald-300">{discount.discount_pct}% off</strong>
                      </p>
                      <p className="text-[10px] text-emerald-400/60 font-mono mt-1">{discount.next_milestone}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-[11px] text-amber-200/80 font-serif leading-relaxed">
                        Buy now at <strong className="text-amber-300">${currentPrice.toFixed(2)}</strong> — as you explore more treasures, the price rises to $14.99
                      </p>
                      {savingsText && (
                        <p className="text-[10px] text-green-400/70 font-mono mt-1 font-semibold">{savingsText} off!</p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Buy button */}
              <button data-testid="btn-sidebar-buy"
                onClick={() => onNavigate('/buy')}
                className="w-full py-3 rounded-full text-sm font-serif font-bold transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_25px_rgba(217,119,6,0.3)] mb-2"
                style={{ background: 'linear-gradient(135deg, #b45309, #d97706)', color: '#fff', boxShadow: '0 4px 15px rgba(217,119,6,0.25)' }}>
                Buy the Ebook
              </button>

              {/* Berean upsell */}
              <button data-testid="btn-sidebar-berean"
                onClick={() => onNavigate('/subscriptions')}
                className="w-full py-2 rounded-full text-[11px] font-serif transition-all border hover:border-blue-400/40 hover:text-blue-300"
                style={{ borderColor: 'rgba(59,130,246,0.2)', color: 'rgba(147,197,253,0.7)' }}>
                or Become a Berean — $9/mo
              </button>
            </div>
          </div>
        </div>

      </div>{/* end flex container */}

      {/* MOBILE STICKY BUY BAR */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t"
        data-testid="buy-mobile-bar"
        style={{ background: 'rgba(10,7,3,0.97)', borderColor: hasDiscount ? 'rgba(16,185,129,0.2)' : 'rgba(217,119,6,0.2)', backdropFilter: 'blur(12px)' }}>
        <div className="flex items-center justify-between px-4 py-3 max-w-lg mx-auto">
          <div>
            <p className="text-[10px] text-amber-600/50 font-mono uppercase tracking-wider">{selectedBook?.title || ''}</p>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold" style={{ color: hasDiscount ? '#34d399' : '#fcd34d' }}>${currentPrice.toFixed(2)}</span>
              {(hasDiscount || !isMaxPrice) && <span className="text-[10px] text-red-400/60 line-through">$14.99</span>}
              {hasDiscount && <span className="text-[9px] text-emerald-400/70 font-mono font-semibold">{discount.discount_pct}% earned</span>}
              {!hasDiscount && savingsText && <span className="text-[9px] text-green-400/60 font-mono font-semibold">{savingsText}</span>}
            </div>
          </div>
          <button data-testid="btn-mobile-buy"
            onClick={() => onNavigate('/buy')}
            className="px-6 py-2.5 rounded-full text-sm font-serif font-bold"
            style={{ background: 'linear-gradient(135deg, #b45309, #d97706)', color: '#fff', boxShadow: '0 4px 15px rgba(217,119,6,0.3)' }}>
            Buy Ebook
          </button>
        </div>
      </div>
    </div>
  );
}
